import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../api.js';
import { useAuthStore } from '../store/auth.js';
import { useT } from '../i18n/useT.js';
import { useAlert } from '../components/AlertProvider.jsx';
import {
  CheckCircle,
  XCircle,
  CreditCard,
  UserCheck,
  BadgeCheck,
  Clock3,
  Layers,
  RefreshCw,
  ShieldAlert,
  CalendarDays,
  Settings2,
  AlertCircle,
} from 'lucide-react';

export default function BillingSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const t = useT();
  const { showConfirm } = useAlert();

  // Settings state
  const [settings, setSettings] = useState({
    manualBillingEnabled: false,
    manualPaymentInstructions: '',
    trialDays: 14,
    defaultPlanId: null,
    enforceHardLimits: false,
  });
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState('');
  const [settingsMsgType, setSettingsMsgType] = useState('');

  // Assign subscription form
  const [assignForm, setAssignForm] = useState({ userId: '', planId: '' });
  const [userQuery, setUserQuery] = useState('');
  const [showUserOptions, setShowUserOptions] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const stats = useMemo(() => {
    const active = subscriptions.filter((item) => item.status === 'active').length;
    const pending = subscriptions.filter((item) => item.status === 'pending_payment' || item.status === 'past_due').length;
    return {
      total: subscriptions.length,
      active,
      pending,
    };
  }, [subscriptions]);

  if (String(user?.role || '').toLowerCase() !== 'admin') {
    return <Navigate to="/templates" replace />;
  }

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, p, subs, usersRes] = await Promise.all([
        api.getBillingSettings(),
        api.getBillingPlans(),
        api.getBillingSubscriptions(),
        api.getUsers(),
      ]);
      setSettings(s);
      setPlans(p.items || []);
      setSubscriptions(subs.items || []);
      setUsers(usersRes.items || []);
    } catch {
      setSettingsMsg(t('billing.loadFailed', 'Failed to load billing data.'));
      setSettingsMsgType('error');
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    setSettingsMsg('');
    setSettingsMsgType('');
    try {
      await api.updateBillingSettings(settings);
      setSettingsMsg(t('billing.settingsSaved', 'Billing settings saved.'));
      setSettingsMsgType('success');
    } catch (err) {
      setSettingsMsg(err.message || t('billing.settingsSaveFailed', 'Could not save billing settings.'));
      setSettingsMsgType('error');
    }
    setSaving(false);
  };

  const handleAssign = async () => {
    const resolvedUserId = assignForm.userId
      || users.find((item) => item.email?.toLowerCase() === userQuery.trim().toLowerCase())?.id
      || '';

    if (!resolvedUserId || !assignForm.planId) return;

    setAssigning(true);
    try {
      await api.createBillingSubscription({
        userId: resolvedUserId,
        planId: assignForm.planId,
      });
      setAssignForm({ userId: '', planId: '' });
      setUserQuery('');
      setSettingsMsg(t('billing.assignSuccess', 'Plan assigned successfully.'));
      setSettingsMsgType('success');
      await loadAll();
    } catch (err) {
      setSettingsMsg(err.message || t('billing.assignFailed', 'Failed to assign plan.'));
      setSettingsMsgType('error');
    }
    setAssigning(false);
  };

  const filteredUsers = users
    .filter((item) => {
      const query = userQuery.trim().toLowerCase();
      if (!query) return true;
      return String(item.email || '').toLowerCase().includes(query)
        || String(item.id || '').toLowerCase().includes(query);
    })
    .slice(0, 10);

  const chooseUser = (user) => {
    setUserQuery(user.email || '');
    setAssignForm((prev) => ({ ...prev, userId: user.id || '' }));
    setShowUserOptions(false);
  };

  const handleConfirmPayment = async (subId) => {
    setActionLoading(`${subId}_confirm`);
    try {
      await api.confirmBillingPayment(subId);
      setSettingsMsg(t('billing.confirmPaymentSuccess', 'Payment confirmed.'));
      setSettingsMsgType('success');
      await loadAll();
    } catch (err) {
      setSettingsMsg(err.message || t('billing.confirmPaymentFailed', 'Failed to confirm payment.'));
      setSettingsMsgType('error');
    }
    setActionLoading('');
  };

  const handleCancelSub = async (subId) => {
    const accepted = await showConfirm(t('billing.confirmCancelSubscriptionHard', 'Cancel this subscription? This action cannot be undone.'), {
      title: t('billing.cancelSubscription', 'Cancel'),
      confirmLabel: t('billing.yes', 'Yes'),
      cancelLabel: t('billing.cancel', 'Cancel'),
      tone: 'warning',
    });
    if (!accepted) return;
    setActionLoading(`${subId}_cancel`);
    try {
      await api.cancelBillingSubscription(subId);
      setSettingsMsg(t('billing.cancelSubscriptionSuccess', 'Subscription canceled.'));
      setSettingsMsgType('success');
      await loadAll();
    } catch (err) {
      setSettingsMsg(err.message || t('billing.cancelSubscriptionFailed', 'Failed to cancel subscription.'));
      setSettingsMsgType('error');
    }
    setActionLoading('');
  };

  const handleReactivate = async (subId) => {
    setActionLoading(`${subId}_reactivate`);
    try {
      await api.updateBillingSubscription(subId, { status: 'active' });
      setSettingsMsg(t('billing.reactivateSuccess', 'Subscription reactivated.'));
      setSettingsMsgType('success');
      await loadAll();
    } catch (err) {
      setSettingsMsg(err.message || t('billing.reactivateFailed', 'Failed to reactivate subscription.'));
      setSettingsMsgType('error');
    }
    setActionLoading('');
  };

  const statusLabel = (status) => {
    const map = {
      active: t('billing.statusActive', 'Active'),
      past_due: t('billing.statusPastDue', 'Past Due'),
      suspended: t('billing.statusSuspended', 'Suspended'),
      canceled: t('billing.statusCanceled', 'Canceled'),
      trialing: t('billing.statusTrialing', 'Trialing'),
      pending_payment: t('billing.statusPendingPayment', 'Pending Payment'),
    };
    return map[status] || status;
  };

  const statusClass = (status) => {
    if (status === 'active') return 'is-active';
    if (status === 'pending_payment' || status === 'past_due') return 'is-warning';
    if (status === 'trialing') return 'is-info';
    if (status === 'suspended') return 'is-danger';
    return 'is-muted';
  };

  const formatPeriod = (start, end) => {
    const s = start ? new Date(start).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    const e = end ? new Date(end).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    return `${s} → ${e}`;
  };

  if (loading) return <div className="page-container"><p>{t('billing.loading', 'Loading...')}</p></div>;

  return (
    <div className="page-container billing-settings-page billing-v2">
      <div className="billing-v2__container">
        <div className="page-header billing-settings-page__header">
          <div>
            <h1>{t('billing.settingsTitle', 'Billing Settings')}</h1>
            <p className="billing-settings-page__subtitle">
              {t('billing.settingsSubtitle', 'Configure billing behavior, trials, and enforcement.')}
            </p>
          </div>
          <button className="btn billing-v2__refresh" onClick={loadAll} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
            {t('billing.refresh', 'Refresh')}
          </button>
        </div>

        <div className="billing-settings-page__stats billing-v2__stats">
          <div className="plans-stat-card">
            <div className="plans-stat-card__label"><span className="billing-v2__stat-icon is-violet"><Layers size={14} /></span> {t('billing.statSubscriptions', 'Subscriptions')}</div>
            <div className="plans-stat-card__value">{stats.total}</div>
          </div>
          <div className="plans-stat-card">
            <div className="plans-stat-card__label"><span className="billing-v2__stat-icon is-green"><BadgeCheck size={14} /></span> {t('billing.statActive', 'Active')}</div>
            <div className="plans-stat-card__value">{stats.active}</div>
          </div>
          <div className="plans-stat-card">
            <div className="plans-stat-card__label"><span className="billing-v2__stat-icon is-amber"><Clock3 size={14} /></span> {t('billing.statPending', 'Pending')}</div>
            <div className="plans-stat-card__value">{stats.pending}</div>
          </div>
        </div>

        <section className="card billing-settings-card billing-v2__section">
          <div className="billing-v2__section-head">
            <h3>{t('billing.configurationTitle', 'Configuration')}</h3>
            <p>{t('billing.configurationSubtitle', 'Control how billing works for new and existing users.')}</p>
          </div>

          <div className="billing-settings-card__grid billing-settings-card__grid--one">
            <div className="billing-v2__toggle-row">
              <div className="billing-v2__toggle-content">
                <span className="billing-v2__toggle-icon"><CreditCard size={14} /></span>
                <div>
                  <label className="billing-settings-card__toggle">
                    {t('billing.manualBilling', 'Manual billing')}
                  </label>
                  <p className="billing-settings-card__hint">
                    {t('billing.manualBillingDesc', 'When enabled, new users require manual payment confirmation.')}
                  </p>
                </div>
              </div>
              <input type="checkbox" checked={settings.manualBillingEnabled}
                onChange={(e) => setSettings({ ...settings, manualBillingEnabled: e.target.checked })} />
            </div>

            <div className="billing-v2__toggle-row">
              <div className="billing-v2__toggle-content">
                <span className="billing-v2__toggle-icon"><ShieldAlert size={14} /></span>
                <div>
                  <label className="billing-settings-card__toggle">
                    {t('billing.enforceHardLimits', 'Enforce hard limits')}
                  </label>
                  <p className="billing-settings-card__hint">
                    {t('billing.enforceHardLimitsDesc', 'Block requests that exceed plan limits (vs. soft warning).')}
                  </p>
                </div>
              </div>
              <input type="checkbox" checked={settings.enforceHardLimits}
                onChange={(e) => setSettings({ ...settings, enforceHardLimits: e.target.checked })} />
            </div>
          </div>

          <div className="billing-settings-card__grid billing-settings-card__grid--two">
            <div className="form-group">
              <label><CalendarDays size={14} /> {t('billing.trialDays', 'Trial days')}</label>
              <input type="number" min={0} max={365} value={settings.trialDays}
                onChange={(e) => setSettings({ ...settings, trialDays: parseInt(e.target.value, 10) || 0 })} />
              <p className="billing-v2__field-hint">{t('billing.trialDaysHint', 'Number of free trial days for new users.')}</p>
            </div>
            <div className="form-group">
              <label><Settings2 size={14} /> {t('billing.defaultPlan', 'Default plan')}</label>
              <select value={settings.defaultPlanId || ''}
                onChange={(e) => setSettings({ ...settings, defaultPlanId: e.target.value || null })}>
                <option value="">{t('billing.selectPlan', 'Select a plan...')}</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>{plan.name} ({plan.code})</option>
                ))}
              </select>
              <p className="billing-v2__field-hint">{t('billing.defaultPlanHint', 'Plan auto-assigned to new editor users.')}</p>
            </div>
          </div>

          <div className="form-group billing-settings-card__instructions">
            <label>{t('billing.paymentInstructions', 'Payment instructions')}</label>
            <textarea rows={3} value={settings.manualPaymentInstructions}
              placeholder={t('billing.paymentInstructionsPlaceholder', 'Wire transfer to account...')}
              onChange={(e) => setSettings({ ...settings, manualPaymentInstructions: e.target.value })} />
            <p className="billing-v2__field-hint">{t('billing.manualPaymentHint', 'Shown to users who need to pay manually.')}</p>
          </div>

          {settingsMsg && (
            <div className={`billing-settings-card__message ${settingsMsgType === 'error' ? 'is-error' : 'is-success'}`}>
              {settingsMsg}
            </div>
          )}

          <button className="btn btn-primary" onClick={handleSaveSettings} disabled={saving}>
            {saving ? (
              <>
                <span className="inline-spinner" />
                {t('billing.saving', 'Saving...')}
              </>
            ) : (
              <>
                <CheckCircle size={14} />
                {t('billing.saveSettings', 'Save settings')}
              </>
            )}
          </button>
        </section>

        <section className="card billing-assignment billing-v2__section">
          <div className="billing-v2__section-head">
            <h3>{t('billing.assignPlanTitle', 'Assign Plan')}</h3>
            <p>{t('billing.assignPlanSubtitle', 'Manually assign a billing plan to a specific user.')}</p>
          </div>

          <div className="billing-assignment__row">
            <div className="form-group">
              <label>{t('billing.user', 'User')}</label>
              <div className="billing-user-select" onBlur={() => setTimeout(() => setShowUserOptions(false), 120)}>
                <input
                  value={userQuery}
                  placeholder={t('billing.userSearchPlaceholder', 'Search user by email...')}
                  onFocus={() => setShowUserOptions(true)}
                  onChange={(e) => {
                    setUserQuery(e.target.value);
                    setAssignForm((prev) => ({ ...prev, userId: '' }));
                    setShowUserOptions(true);
                  }}
                />
                {showUserOptions && (
                  <div className="billing-user-select__menu">
                    {filteredUsers.length === 0 ? (
                      <div className="billing-user-select__empty">{t('billing.noUsersFound', 'No users found.')}</div>
                    ) : filteredUsers.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className="billing-user-select__option"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => chooseUser(item)}
                      >
                        <span>{item.email}</span>
                        <small>{item.id}</small>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>{t('billing.plan', 'Plan')}</label>
              <select value={assignForm.planId}
                onChange={(e) => setAssignForm({ ...assignForm, planId: e.target.value })}>
                <option value="">{t('billing.selectPlan', 'Select a plan...')}</option>
                {plans.filter((plan) => plan.isActive).map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} ({plan.code})
                  </option>
                ))}
              </select>
            </div>
            <button className="btn btn-primary billing-assignment__button" onClick={handleAssign} disabled={assigning || (!assignForm.userId && !userQuery.trim()) || !assignForm.planId}>
              {assigning ? <span className="inline-spinner" /> : <UserCheck size={14} />}
              {t('billing.assignPlan', 'Assign Plan')}
            </button>
          </div>
        </section>

        <div className="card billing-subscriptions-wrap billing-v2__section">
          <div className="billing-v2__section-head">
            <h3>{t('billing.subscriptions', 'Subscriptions')}</h3>
            <p>{t('billing.subscriptionsTotal', '{{count}} subscriptions total.', { count: subscriptions.length })}</p>
          </div>

        <table className="data-table">
          <thead>
            <tr>
              <th>{t('billing.user', 'User')}</th>
              <th>{t('billing.plan', 'Plan')}</th>
              <th>{t('billing.status', 'Status')}</th>
              <th>{t('billing.period', 'Period')}</th>
              <th>{t('billing.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.map((sub) => (
              <tr key={sub.id}>
                <td>{sub.userEmail || sub.userId || '—'}</td>
                <td>{sub.planName || sub.planCode || '—'}</td>
                <td>
                  <span className={`billing-status-pill ${statusClass(sub.status)}`}>
                    {statusLabel(sub.status)}
                  </span>
                </td>
                <td className="billing-subscriptions__period">
                  <span className="billing-v2__period-inner">
                    <CalendarDays size={12} />
                    {formatPeriod(sub.currentPeriodStart, sub.currentPeriodEnd)}
                  </span>
                </td>
                <td>
                  <div className="billing-subscriptions__actions">
                    {(sub.status === 'pending_payment' || sub.status === 'past_due') && (
                      <button className="btn btn-icon" title={t('billing.confirmPayment', 'Confirm Payment')}
                        onClick={() => handleConfirmPayment(sub.id)} disabled={!!actionLoading}>
                        {actionLoading === `${sub.id}_confirm` ? <span className="inline-spinner" /> : <CheckCircle size={14} />}
                      </button>
                    )}
                    {sub.status === 'suspended' && (
                      <button className="btn btn-icon" title={t('billing.reactivate', 'Reactivate')}
                        onClick={() => handleReactivate(sub.id)} disabled={!!actionLoading}>
                        {actionLoading === `${sub.id}_reactivate` ? <span className="inline-spinner" /> : <RefreshCw size={14} />}
                      </button>
                    )}
                    {sub.status !== 'canceled' && (
                      <button className="btn btn-icon btn-danger" title={t('billing.cancelSubscription', 'Cancel')}
                        onClick={() => handleCancelSub(sub.id)} disabled={!!actionLoading}>
                        {actionLoading === `${sub.id}_cancel` ? <span className="inline-spinner" /> : <XCircle size={14} />}
                      </button>
                    )}
                    {sub.status === 'canceled' && <span className="billing-v2__muted">—</span>}
                  </div>
                </td>
              </tr>
            ))}
            {subscriptions.length === 0 && (
              <tr>
                <td colSpan={5} className="plans-table__empty">
                  <div className="billing-v2__empty">
                    <AlertCircle size={20} />
                    <span>{t('billing.noSubscriptions', 'No subscriptions yet.')}</span>
                  </div>
                </td>
              </tr>
            )}
            {!!actionLoading && (
              <tr>
                <td colSpan={5} className="billing-v2__working">{t('billing.processing', 'Processing...')}</td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
